

/***************************************************************************************************
 * 
 *  >>> EMAIL VERIFICATION FIX - COMPLETE SOLUTION <<<
 *
 *  **Problem:**
 *  When an admin changes a user's email, the verification email was not being sent to the new address,
 *  blocking the user from verifying their account.
 *
 *  ✅ **Solution Implemented:**
 *  This file contains the complete fix across both the backend Cloud Function and the frontend API service.
 *
 *  **BACKEND (`adminUpdateUser` Cloud Function):**
 *  1. When an email is changed, the function now generates a secure email verification link using `admin.auth().generateEmailVerificationLink()`.
 *  2. This link is stored temporarily in the Realtime Database at `/users/{uid}/pendingEmailVerification`.
 *  3. The function now handles all profile data updates atomically, preventing race conditions.
 *
 *  **FRONTEND (API Service & UI):**
 *  1. The `updateUser` function now passes all data to the `adminUpdateUser` cloud function.
 *  2. The `EmailVerificationModal` automatically re-sends the verification email upon appearing, ensuring the user gets the link.
 *  3. The `AuthProvider` context automatically cleans up the temporary verification link from the database after a successful verification.
 * 
 ***************************************************************************************************
 * 
 *  --- START OF CLOUD FUNCTIONS CODE --- (COPY/PASTE THIS INTO `functions/src/index.ts`)
 *
 *  import { onCall, HttpsError, type CallableRequest } from "firebase-functions/v2/https";
 *  import * as admin from "firebase-admin";
 *
 *  admin.initializeApp();
 *
 *  // Security helper to ensure the caller is an authenticated admin.
 *  const ensureAdmin = async (request: CallableRequest) => {
 *      if (!request.auth) {
 *          throw new HttpsError("unauthenticated", "Authentication is required to perform this action.");
 *      }
 *      const adminRef = admin.database().ref(`/users/${request.auth.uid}`);
 *      const snapshot = await adminRef.once("value");
 *      if (snapshot.val()?.role !== "ADMIN") {
 *          throw new HttpsError("permission-denied", "You must be an admin to perform this action.");
 *      }
 *  };
 *
 *  // --- `adminCreateUser` Function ---
 *  export const adminCreateUser = onCall(async (request) => {
 *      await ensureAdmin(request);
 *  
 *      const { email, password, name, role, teCode, countryCode, phone, city } = request.data;
 *      if (!email || !password) {
 *          throw new HttpsError("invalid-argument", "Email and password are required.");
 *      }
 *  
 *      try {
 *          const userRecord = await admin.auth().createUser({
 *              email,
 *              password,
 *              displayName: name,
 *              emailVerified: false,
 *          });
 *  
 *          const dbData = {
 *              name: name || '',
 *              email: email,
 *              role: role || 'CLIENT',
 *              teCode: teCode || '',
 *              countryCode: countryCode || '',
 *              phone: phone || '',
 *              city: city || '',
 *              isVerified: false,
 *              isEmailVerified: false,
 *              isLocked: false,
 *              id: userRecord.uid
 *          };
 *  
 *          await admin.database().ref(`/users/${userRecord.uid}`).set(dbData);
 *  
 *          return { success: true, uid: userRecord.uid };
 *      } catch (error: any) {
 *          console.error("Error in adminCreateUser:", error);
 *          if (error.code === 'auth/email-already-exists') {
 *              throw new HttpsError("already-exists", "This email address is already in use by another account.");
 *          }
 *          if (error.code === 'auth/invalid-password') {
 *              throw new HttpsError("invalid-argument", "Password must be at least 6 characters long.");
 *          }
 *          throw new HttpsError("internal", error.message || "An unknown server error occurred while creating the user.");
 *      }
 *  });
 *
 *  // --- `adminUpdateUser` Function (UPDATED) ---
 *  export const adminUpdateUser = onCall(async (request) => {
 *      await ensureAdmin(request);
 *  
 *      const { uid, email, password, ...profileData } = request.data;
 *      if (!uid) {
 *          throw new HttpsError("invalid-argument", "A User ID (uid) is required.");
 *      }
 *  
 *      try {
 *          const authUpdatePayload: admin.auth.UpdateRequest = {};
 *          const dbUpdatePayload: any = { ...profileData };
 *          let emailChanged = false;
 *  
 *          const userRecord = await admin.auth().getUser(uid);
 *  
 *          // Handle email change
 *          if (email && userRecord.email !== email) {
 *              authUpdatePayload.email = email;
 *              authUpdatePayload.emailVerified = false;
 *              dbUpdatePayload.email = email;
 *              dbUpdatePayload.isEmailVerified = false;
 *              emailChanged = true;
 *          }
 *  
 *          if (password) {
 *              authUpdatePayload.password = password;
 *          }
 *  
 *          // Apply Auth updates if any
 *          if (Object.keys(authUpdatePayload).length > 0) {
 *              await admin.auth().updateUser(uid, authUpdatePayload);
 *          }
 *  
 *          // Apply profile data updates to RTDB
 *          if (Object.keys(dbUpdatePayload).length > 0) {
 *              await admin.database().ref(`/users/${uid}`).update(dbUpdatePayload);
 *          }
 *  
 *          // If email was changed, generate and store verification link
 *          if (emailChanged && email) {
 *              const actionCodeSettings = {
 *                  url: `https://tagexpress-474310.web.app/#/verify-email`,
 *                  handleCodeInApp: true,
 *              };
 *              const verificationLink = await admin.auth().generateEmailVerificationLink(email, actionCodeSettings);
 *              await admin.database().ref(`/users/${uid}/pendingEmailVerification`).set({
 *                  link: verificationLink,
 *                  email: email,
 *                  createdAt: admin.database.ServerValue.TIMESTAMP,
 *              });
 *          }
 *  
 *          return { success: true, emailChanged };
 *      } catch (error: any) {
 *          console.error("Error in adminUpdateUser:", error);
 *          if (error.code === 'auth/email-already-exists') {
 *              throw new HttpsError("already-exists", "This email address is already in use by another account.");
 *          }
 *          if (error.code === 'auth/invalid-password') {
 *              throw new HttpsError("invalid-argument", "Password must be at least 6 characters long.");
 *          }
 *          throw new HttpsError("internal", error.message || "An unknown server error occurred while updating the user.");
 *      }
 *  });
 *
 *  // --- `deleteUser` Function ---
 *  export const deleteUser = onCall(async (request) => {
 *      await ensureAdmin(request);
 *  
 *      const { uid } = request.data;
 *      if (!uid) {
 *          throw new HttpsError("invalid-argument", "A User ID (uid) is required for deletion.");
 *      }
 *      if (uid === request.auth?.uid) {
 *          throw new HttpsError("permission-denied", "Admins cannot delete their own account through this function.");
 *      }
 *  
 *      try {
 *          await admin.auth().deleteUser(uid);
 *          await admin.database().ref(`/users/${uid}`).remove();
 *          return { success: true };
 *      } catch (error: any) {
 *          console.error("Error in deleteUser:", error);
 *          if (error.code === 'auth/user-not-found') {
 *              await admin.database().ref(`/users/${uid}`).remove();
 *              return { success: true };
 *          }
 *          throw new HttpsError("internal", error.message || "An internal server error occurred while deleting the user.");
 *      }
 *  });
 *
 ***************************************************************************************************/


import { 
    ref,
    get,
    set,
    update,
    push,
    onValue,
    query,
    orderByChild,
    equalTo,
    DataSnapshot
} from 'firebase/database';
import { 
    ref as storageRef,
    uploadBytes,
    getDownloadURL
} from 'firebase/storage';
// FIX: Aliased firebase auth function imports to avoid name collisions with local wrapper functions.
import { 
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword, 
    signOut, 
    sendPasswordResetEmail as firebaseSendPasswordResetEmail, 
    confirmPasswordReset as firebaseConfirmPasswordReset,
    verifyPasswordResetCode as firebaseVerifyPasswordResetCode,
    applyActionCode as firebaseApplyActionCode,
    sendEmailVerification
} from 'firebase/auth';
import { db, auth, storage, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { User, Order, Quote, Notification, ChatMessage, Warehouse, NotificationPreference, UserRole, OrderStatus, PaymentStatus, OrderType, PriceCheckRequest } from '../types';
import { GoogleGenAI } from '@google/genai';

// --- Callable Cloud Functions ---
const adminCreateUser = httpsCallable(functions, 'adminCreateUser');
const adminUpdateUser = httpsCallable(functions, 'adminUpdateUser');
const adminDeleteUser = httpsCallable(functions, 'deleteUser');


// --- Security Utilities ---

// 1. Sanitization: Prevent XSS by stripping dangerous tags from inputs
const sanitize = (input: any): any => {
    if (typeof input === 'string') {
        return input
            .replace(/<script\b[^>]*>([\s\S]*?)<\/script>/gim, "") // Remove script tags
            .replace(/on\w+="[^"]*"/g, "") // Remove event handlers like onclick
            .replace(/javascript:/g, "") // Remove javascript: protocol
            .trim();
    }
    if (typeof input === 'object' && input !== null) {
        // Recursively sanitize objects
        if (Array.isArray(input)) {
            return input.map(i => sanitize(i));
        }
        const sanitizedObj: any = {};
        Object.keys(input).forEach(key => {
            // Filter out undefined values to prevent Firebase errors
            if (input[key] !== undefined) {
                sanitizedObj[key] = sanitize(input[key]);
            }
        });
        return sanitizedObj;
    }
    return input;
};

// 2. Rate Limiting: Prevent DDoS/Spam by throttling actions
const rateLimitMap = new Map<string, number>();
const checkRateLimit = (action: string, limitMs: number = 2000) => {
    const userKey = auth.currentUser?.uid || 'anonymous';
    const compositeKey = `${userKey}:${action}`;
    const now = Date.now();
    const last = rateLimitMap.get(compositeKey) || 0;
    
    if (now - last < limitMs) {
        throw new Error(`Please wait ${Math.ceil((limitMs - (now - last))/1000)}s before trying again.`);
    }
    rateLimitMap.set(compositeKey, now);
};

// --- Helper Functions ---

// Helper to get the current user's role from the database
const getCurrentUserRole = async (): Promise<UserRole | null> => {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    const userSnapshot = await get(ref(db, `users/${currentUser.uid}`));
    if (userSnapshot.exists()) {
        return userSnapshot.val().role;
    }
    return null;
}


// Helper to convert RTDB snapshot to array
const snapshotToArray = <T>(snapshot: DataSnapshot): T[] => {
    const data = snapshot.val();
    if (!data) return [];
    // CRITICAL FIX: Spread data first, then overwrite 'id' with the key. 
    // This ensures the ID is always the database key, and not overwritten by potential bad data in the document.
    return Object.keys(data).map(key => ({ ...data[key], id: key }));
};

// Helper to ensure Order objects have all array fields initialized
const sanitizeOrder = (data: any): Order => {
    if (!data) return data;
    
    // The main fix is here. `data.quotes` from Firebase is an object with unique keys.
    // We convert it to an array of objects, and ensure each quote object has the Firebase key as its `id`.
    const quotesObject = data.quotes;
    const quotesArray = quotesObject ? Object.keys(quotesObject).map(key => ({
        ...quotesObject[key],
        id: key,
    })) : [];
    
    // Ensure priceCheckRequests is always a dense array, filtering out any null/undefined from sparse arrays.
    const priceCheckRequests = data.priceCheckRequests 
      ? (Array.isArray(data.priceCheckRequests) 
        ? data.priceCheckRequests.filter(Boolean) 
        : Object.values(data.priceCheckRequests).filter(Boolean)) 
      : [];

    return {
       ...data,
       quotes: quotesArray,
       statusHistory: data.statusHistory || [],
       messages: data.messages ? (Array.isArray(data.messages) ? data.messages : Object.values(data.messages)) : [],
       priceCheckRequests: priceCheckRequests,
       priceCheckStaffIds: data.priceCheckStaffIds || {},
    };
};

// ... existing code in api.ts

// --- User Management (Admin) ---

// New function for admin to create a user
export const createUser = async (userData: Partial<User>): Promise<void> => {
    const currentUserRole = await getCurrentUserRole();
    if (currentUserRole !== UserRole.Admin) {
        throw new Error('Permission denied.');
    }
    await adminCreateUser(sanitize(userData));
};

// New function for admin to update any user
export const updateUser = async (uid: string, data: Partial<User>): Promise<any> => {
    const currentUserRole = await getCurrentUserRole();
    if (currentUserRole !== UserRole.Admin) {
        if (auth.currentUser?.uid === uid) {
            await updateProfile(data);
            return { data: { success: true, emailChanged: false } }; // Mimic cloud function response
        }
        throw new Error('Permission denied.');
    }

    const sanitizedData = sanitize(data);
    
    // Call the single cloud function with all data for the update
    const result = await adminUpdateUser({ uid, ...sanitizedData });

    return result.data; // e.g., { success: true, emailChanged: true }
};


// New function for admin to delete a user
export const deleteUser = async (uid: string): Promise<void> => {
    const currentUserRole = await getCurrentUserRole();
    if (currentUserRole !== UserRole.Admin) {
        throw new Error('Permission denied.');
    }
    await adminDeleteUser({ uid });
};

// --- The rest of your existing api.ts file ---
// ... (I'm assuming the rest of the file content from the prompt)
// ...
// --- Authentication ---

export const login = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
};

export const signup = async (userData: Partial<User>): Promise<User> => {
    const { email, password, name, city, countryCode, phone } = sanitize(userData);
    if (!email || !password || !name) {
        throw new Error("Missing required signup fields");
    }
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const teCode = `TE-${userCredential.user.uid.substring(0, 6).toUpperCase()}`;
    const newUser: User = {
        id: userCredential.user.uid,
        name,
        email,
        city: city || '',
        countryCode: countryCode || '',
        phone: phone || '',
        role: UserRole.Client,
        isVerified: false,
        isEmailVerified: false,
        isLocked: false,
        teCode,
    };
    await set(ref(db, `users/${userCredential.user.uid}`), newUser);
    await sendEmailVerification(userCredential.user);
    return newUser;
};

export const logout = () => {
    return signOut(auth);
};

export const sendPasswordResetEmail = (email: string) => {
    return firebaseSendPasswordResetEmail(auth, email);
};

export const verifyPasswordResetCode = (code: string) => {
    return firebaseVerifyPasswordResetCode(auth, code);
};

export const confirmPasswordReset = (code: string, newPassword: string) => {
    return firebaseConfirmPasswordReset(auth, code, newPassword);
};

export const applyActionCode = (code: string) => {
    return firebaseApplyActionCode(auth, code);
};

export const resendVerificationEmail = () => {
    if (auth.currentUser) {
        return sendEmailVerification(auth.currentUser);
    }
    throw new Error("No user is currently signed in.");
};

// --- User Data ---

export const getUsers = async (): Promise<User[]> => {
    const snapshot = await get(ref(db, 'users'));
    return snapshotToArray<User>(snapshot);
};

export const listenToUsers = (callback: (users: User[]) => void): (() => void) => {
    const usersRef = ref(db, 'users');
    return onValue(usersRef, (snapshot) => {
        callback(snapshotToArray<User>(snapshot));
    });
};


// --- Orders ---

export const getOrderById = async (id: string): Promise<Order | null> => {
    const snapshot = await get(ref(db, `orders/${id}`));
    return snapshot.exists() ? sanitizeOrder(snapshot.val()) : null;
};

export const listenToOrderById = (id: string, callback: (order: Order | null) => void): (() => void) => {
    const orderRef = ref(db, `orders/${id}`);
    return onValue(orderRef, (snapshot) => {
        callback(snapshot.exists() ? { ...sanitizeOrder(snapshot.val()), id } : null);
    });
};


export const getOrders = async (): Promise<Order[]> => {
    const snapshot = await get(ref(db, 'orders'));
    return snapshotToArray<Order>(snapshot).map(sanitizeOrder);
};

export const listenToOrders = (currentUser: User, callback: (orders: Order[]) => void): (() => void) => {
    if (currentUser.role === UserRole.Admin) {
        const queryRef = ref(db, 'orders');
        return onValue(queryRef, (snapshot) => {
            const orders = snapshotToArray<Order>(snapshot).map(sanitizeOrder);
            const sortedOrders = orders.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            callback(sortedOrders);
        });
    }

    if (currentUser.role === UserRole.Client) {
        const queryRef = query(ref(db, 'orders'), orderByChild('clientId'), equalTo(currentUser.id));
        return onValue(queryRef, (snapshot) => {
            const orders = snapshotToArray<Order>(snapshot).map(sanitizeOrder);
            const sortedOrders = orders.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            callback(sortedOrders);
        });
    }

    if (currentUser.role === UserRole.Staff) {
        const directOrdersMap = new Map<string, Order>();
        const priceCheckOrdersMap = new Map<string, Order>();
        const listeners: (() => void)[] = [];

        const updateCallback = () => {
            // Merge both maps and deduplicate by ID
            const allOrders = [...Array.from(directOrdersMap.values()), ...Array.from(priceCheckOrdersMap.values())];
            const uniqueMap = new Map<string, Order>();
            allOrders.forEach(o => uniqueMap.set(o.id, o));
            
            const sortedOrders = Array.from(uniqueMap.values())
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            callback(sortedOrders);
        };

        // Listener 1: Directly assigned orders
        const directAssignQuery = query(ref(db, 'orders'), orderByChild('assignedStaffId'), equalTo(currentUser.id));
        const unsub1 = onValue(directAssignQuery, (snapshot) => {
            directOrdersMap.clear();
            snapshot.forEach(childSnapshot => {
                directOrdersMap.set(childSnapshot.key!, { ...sanitizeOrder(childSnapshot.val()), id: childSnapshot.key! });
            });
            updateCallback();
        }, (error) => console.error("Direct assignment listener error:", error));
        listeners.push(unsub1);

        // Listener 2: Price check orders
        const priceCheckQuery = query(ref(db, 'orders'), orderByChild('status'), equalTo(OrderStatus.PriceChecking));
        const unsub2 = onValue(priceCheckQuery, (snapshot) => {
            priceCheckOrdersMap.clear();
            snapshot.forEach(childSnapshot => {
                const order = { ...sanitizeOrder(childSnapshot.val()), id: childSnapshot.key! };
                if (order.priceCheckStaffIds?.[currentUser.id]) {
                    priceCheckOrdersMap.set(childSnapshot.key!, order);
                }
            });
            updateCallback();
        }, (error) => console.error("Price check listener error:", error));
        listeners.push(unsub2);

        return () => {
            listeners.forEach(unsub => unsub());
        };
    }


    // Default case: return an empty listener if role is not recognized or an error occurs
    return () => {};
};


export const createOrder = async (orderData: Partial<Order>): Promise<string> => {
    const newOrderRef = push(ref(db, 'orders'));
    const orderId = `TX-${new Date().getFullYear()}-${String(newOrderRef.key).slice(-4)}`;
    const now = new Date().toISOString();
    
    const newOrder: Order = {
        id: newOrderRef.key!,
        clientId: auth.currentUser!.uid,
        status: OrderStatus.Received,
        paymentStatus: PaymentStatus.Pending,
        createdAt: now,
        updatedAt: now,
        quotes: [],
        statusHistory: [{ status: OrderStatus.Received, timestamp: now }],
        orderType: orderData.orderType || OrderType.General,
        orderId,
        isQuoteEnabled: true,
        ...sanitize(orderData),
    };

    await set(newOrderRef, newOrder);
    return newOrderRef.key!;
};

export const updateOrder = async (id: string, updates: Partial<Order>): Promise<void> => {
    const sanitizedUpdates = sanitize(updates);
    const orderRef = ref(db, `orders/${id}`);
    
    const existingOrder = await get(orderRef);
    if (!existingOrder.exists()) {
        throw new Error("Order not found");
    }

    const orderData = existingOrder.val();
    
    // Add to status history if status is changing
    if (sanitizedUpdates.status && sanitizedUpdates.status !== orderData.status) {
        const now = new Date().toISOString();
        sanitizedUpdates.statusHistory = [
            ...(orderData.statusHistory || []),
            { status: sanitizedUpdates.status, timestamp: now }
        ];
    }
    
    sanitizedUpdates.updatedAt = new Date().toISOString();
    
    await update(orderRef, sanitizedUpdates);
};


export const addQuoteToOrder = async (orderId: string, quote: Quote): Promise<void> => {
    const quotesRef = ref(db, `orders/${orderId}/quotes`);
    const newQuoteRef = push(quotesRef);
    await set(newQuoteRef, quote);
};

export const addMessageToOrder = async (orderId: string, text: string, imageUrl?: string): Promise<void> => {
    const messagesRef = ref(db, `orders/${orderId}/messages`);
    const newMessageRef = push(messagesRef);
    const message: Partial<ChatMessage> = {
        id: newMessageRef.key!,
        senderId: auth.currentUser!.uid,
        text: sanitize(text),
        createdAt: new Date().toISOString(),
    };

    if (imageUrl) {
        message.imageUrl = imageUrl;
    }

    await set(newMessageRef, message as ChatMessage);
};

export const listenToMessagesForOrder = (orderId: string, callback: (messages: ChatMessage[]) => void): (() => void) => {
    const messagesRef = ref(db, `orders/${orderId}/messages`);
    return onValue(messagesRef, (snapshot) => {
        callback(snapshotToArray<ChatMessage>(snapshot));
    });
};

// --- Warehouses ---

export const getWarehouses = async (): Promise<Warehouse[]> => {
    const snapshot = await get(ref(db, 'warehouses'));
    return snapshotToArray<Warehouse>(snapshot);
};

// --- Notifications ---

export const getNotificationPreferences = async (): Promise<NotificationPreference[]> => {
    const snapshot = await get(ref(db, 'notificationPreferences'));
    return snapshotToArray<NotificationPreference>(snapshot);
};

export const updateNotificationPreferences = async (preferences: NotificationPreference[]): Promise<void> => {
    const updates = {};
    preferences.forEach(p => {
        updates[p.id] = p;
    });
    await update(ref(db, 'notificationPreferences'), updates);
};

export const getNotifications = async (): Promise<Notification[]> => {
    if (!auth.currentUser) return [];
    const q = query(ref(db, 'notifications'), orderByChild('userId'), equalTo(auth.currentUser.uid));
    const snapshot = await get(q);
    return snapshotToArray<Notification>(snapshot).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
};

export const listenToNotifications = (callback: (notifications: Notification[]) => void): (() => void) => {
    if (!auth.currentUser) return () => {};
    const q = query(ref(db, 'notifications'), orderByChild('userId'), equalTo(auth.currentUser.uid));
    return onValue(q, (snapshot) => {
        const notifications = snapshotToArray<Notification>(snapshot).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        callback(notifications);
    });
};

export const markNotificationAsRead = async (id: string): Promise<void> => {
    await update(ref(db, `notifications/${id}`), { isRead: true });
};

export const markNotificationsAsRead = async (): Promise<void> => {
    const notifications = await getNotifications();
    const updates = {};
    notifications.forEach(n => {
        if (!n.isRead) {
            updates[`${n.id}/isRead`] = true;
        }
    });
    if (Object.keys(updates).length > 0) {
        await update(ref(db, 'notifications'), updates);
    }
};

export const sendNotification = async (notification: Omit<Notification, 'id' | 'isRead' | 'createdAt'>): Promise<void> => {
    const newNotifRef = push(ref(db, 'notifications'));
    const newNotification: Notification = {
        id: newNotifRef.key!,
        isRead: false,
        createdAt: new Date().toISOString(),
        ...notification
    };
    await set(newNotifRef, newNotification);
};

// --- AI & Misc ---
export const getSourcingSuggestions = async (description: string) => {
    const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY! });
    const prompt = `Based on the user request "${description}", suggest a concise product name and detailed notes for a sourcing agent. The notes should include potential materials, quality standards, quantity considerations, and any other relevant specifics for sourcing from China or UAE. Format the output as a JSON object with keys "productName" and "suggestedNotes".`;

    try {
        const response = await ai.models.generateContent({
            model: 'gemini-3-flash-preview',
            contents: [{ parts: [{ text: prompt }] }],
        });

        const text = response.text || '';
        const jsonString = text.replace(/```json|```/g, '').trim();
        return JSON.parse(jsonString);

    } catch (error) {
        console.error("AI suggestion error:", error);
        throw new Error("Failed to get suggestions from AI assistant.");
    }
};

export const uploadFile = async (file: File, path: string): Promise<string> => {
    checkRateLimit('uploadFile', 5000);
    const filePath = `${path}/${auth.currentUser!.uid}/${Date.now()}-${file.name}`;
    const fileRef = storageRef(storage, filePath);
    await uploadBytes(fileRef, file);
    return getDownloadURL(fileRef);
};


// --- Self-service Profile Updates ---
export const changePassword = async () => {
    // This requires re-authentication which is handled by Firebase automatically when using this function.
    // It is a client-side only operation.
    // Note: This needs to be implemented. For now, it's a placeholder.
    // await reauthenticateWithCredential...
    // await updatePassword...
    throw new Error('Password change not yet implemented.');
};

export const updateProfile = async (data: Partial<User>): Promise<Partial<User>> => {
    if (!auth.currentUser) throw new Error("Not authenticated");
    
    const allowedFields = ['name', 'city', 'countryCode', 'phone'];
    const updates: Partial<User> = {};
    
    allowedFields.forEach(field => {
        if(data[field] !== undefined) {
            updates[field] = data[field];
        }
    });

    if (Object.keys(updates).length === 0) {
        return {};
    }

    const sanitizedData = sanitize(updates);
    await update(ref(db, `users/${auth.currentUser.uid}`), sanitizedData);
    return sanitizedData;
};

// --- Staff Price Checking ---
export const sendForPriceCheck = async (orderId: string, staffIds: string[]): Promise<void> => {
    const orderRef = ref(db, `orders/${orderId}`);
    const orderSnapshot = await get(orderRef);
    if (!orderSnapshot.exists()) throw new Error("Order not found");

    const orderData = orderSnapshot.val();
    const existingRequests = orderData.priceCheckRequests || [];
    const newRequests = staffIds.map(staffId => ({ staffId, status: 'pending' }));
    
    const updates: { [key: string]: any } = {
        status: OrderStatus.PriceChecking,
        priceCheckRequests: [...existingRequests, ...newRequests],
    };
    for (const staffId of staffIds) {
        updates[`priceCheckStaffIds/${staffId}`] = true;
    }
    
    await update(orderRef, updates);

    // Send notifications
    for (const staffId of staffIds) {
        // FIX: Added missing 'message' property to satisfy the Notification type.
        await sendNotification({
            userId: staffId,
            message: '', // Fallback message required by type
            messageKey: 'notification_new_price_check',
            messageParams: { orderId: orderData.orderId },
            link: '/price-check-list'
        });
    }
};

export const submitPriceCheck = async (orderId: string, priceData: Omit<PriceCheckRequest, 'staffId' | 'status' | 'submittedAt'>): Promise<void> => {
    const staffId = auth.currentUser!.uid;
    const orderRef = ref(db, `orders/${orderId}`);
    const orderSnapshot = await get(orderRef);
    if (!orderSnapshot.exists()) throw new Error("Order not found");

    const orderData = orderSnapshot.val();
    const rawRequests = orderData.priceCheckRequests || {};

    // Find the key (index) of the price check request for the current user.
    const requestKey = Object.keys(rawRequests).find(key =>
        rawRequests[key] && rawRequests[key].staffId === staffId && rawRequests[key].status === 'pending'
    );

    if (!requestKey) {
        throw new Error("No pending price check found for you on this order.");
    }

    const updatedRequestData = {
        ...rawRequests[requestKey],
        ...sanitize(priceData),
        status: 'submitted' as const,
        submittedAt: new Date().toISOString()
    };
    
    // Atomically update only the specific item in the array-like object.
    const updates: { [key: string]: any } = {};
    updates[`/orders/${orderId}/priceCheckRequests/${requestKey}`] = updatedRequestData;
    await update(ref(db), updates);

    // Check if all requests are submitted to notify the admin/assigned staff.
    const allRequestsAfterUpdate = { ...rawRequests, [requestKey]: updatedRequestData };
    // FIX: Explicitly type `req` as `any` to prevent TypeScript from inferring it as `unknown`.
    // This allows accessing `req.status` without a type error.
    const allSubmitted = Object.values(allRequestsAfterUpdate).every((req: any) => req && req.status === 'submitted');

    if (allSubmitted && orderData.assignedStaffId) {
        await sendNotification({
            userId: orderData.assignedStaffId,
            message: '', // Fallback message required by type
            messageKey: 'notification_all_prices_submitted',
            messageParams: { orderId: orderData.orderId },
            link: `/orders/${orderId}`
        });
    }
};


export const api = {
    login,
    signup,
    logout,
    sendPasswordResetEmail,
    verifyPasswordResetCode,
    confirmPasswordReset,
    applyActionCode,
    resendVerificationEmail,
    getUsers,
    listenToUsers,
    createUser,
    updateUser,
    deleteUser,
    getOrderById,
    listenToOrderById,
    getOrders,
    listenToOrders,
    createOrder,
    updateOrder,
    addQuoteToOrder,
    addMessageToOrder,
    listenToMessagesForOrder,
    getWarehouses,
    getNotificationPreferences,
    updateNotificationPreferences,
    getNotifications,
    listenToNotifications,
    markNotificationAsRead,
    markNotificationsAsRead,
    sendNotification,
    getSourcingSuggestions,
    uploadFile,
    changePassword,
    updateProfile,
    sendForPriceCheck,
    submitPriceCheck,
};
